import { useMemo } from 'react';

import { useUiPreferencesForUser } from '@hcs/huell';
import {
  ExperienceFlagIds,
  ExperienceFlagsType,
  PreferencesKeys,
} from '@hcs/types';
import { ENV, IS_LOCAL } from '@hcs/webapps';

import { EXPERIENCE_FLAG_CONFIGS } from '../configs';
import { useExperienceFlagsDevLocalStorageState } from '../hooks/useExperienceFlagsDevLocalStorageState';

import { useExperienceFlagsSlice } from './useExperienceFlagsSlice';

export const useExperienceFlagsDevtools = () => {
  const { state: localStorageState } = useExperienceFlagsDevLocalStorageState();
  const { state: sliceState } = useExperienceFlagsSlice();

  return useMemo(() => {
    return {
      ...sliceState,
      ...localStorageState,
    };
  }, [sliceState, localStorageState]);
};

export const useExperienceFlagsDevtoolsWithoutForce = () => {
  const devtoolsState = useExperienceFlagsDevtools();

  return useMemo(() => {
    const results: Partial<ExperienceFlagsType> = {};
    Object.entries(devtoolsState).forEach(
      ([experienceFlagIdStr, flagDevtoolState]) => {
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        const experienceFlagId = experienceFlagIdStr as ExperienceFlagIds;
        results[experienceFlagId] = flagDevtoolState.value;
      },
    );
    return results;
  }, [devtoolsState]);
};

export const useExperienceFlagsSystem = () => {
  return useMemo<ExperienceFlagsType>(() => {
    const results: Partial<ExperienceFlagsType> = {};

    for (const expFlagConfig of Object.values(EXPERIENCE_FLAG_CONFIGS)) {
      results[expFlagConfig.experienceFlagId] =
        IS_LOCAL || ENV === 'dev'
          ? expFlagConfig.envs.dev
          : expFlagConfig.envs[ENV];
    }
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return results as ExperienceFlagsType;
  }, []);
};

export const useExperienceFlagsPreferenceControlled = () => {
  const experienceFlagsSystem = useExperienceFlagsSystem();
  const { data: uiPreferencesForUser } = useUiPreferencesForUser();
  return useMemo<Partial<ExperienceFlagsType>>(() => {
    const data: Partial<ExperienceFlagsType> = {
      // we allow an individual user to turn off the pexp redesign
      PEXP_REDESIGN:
        uiPreferencesForUser?.[PreferencesKeys.PexpRedesignEnabled] === false
          ? false
          : uiPreferencesForUser?.[PreferencesKeys.PexpRedesignEnabled] === true
            ? true
            : experienceFlagsSystem.PEXP_REDESIGN,
    };
    return data;
  }, [uiPreferencesForUser, experienceFlagsSystem]);
};

export const useExperienceFlagsWithoutDevTool = (): ExperienceFlagsType => {
  const experienceFlagsSystem = useExperienceFlagsSystem();
  const experienceFlagsPreferences = useExperienceFlagsPreferenceControlled();
  return useMemo(() => {
    return {
      ...experienceFlagsSystem,
      // Use preferences to allow users to opt-in to beta experiences:
      ...experienceFlagsPreferences,
    };
  }, [experienceFlagsSystem, experienceFlagsPreferences]);
};

// Using this hook allows us to augment the env variables that initialize the rtk slice state
export const useExperienceFlags = (): ExperienceFlagsType => {
  const experienceFlagsSystem = useExperienceFlagsSystem();
  const experienceFlagsPreferences = useExperienceFlagsPreferenceControlled();
  const experienceFlagsDevtools = useExperienceFlagsDevtoolsWithoutForce();
  return useMemo(() => {
    return {
      ...experienceFlagsSystem,
      // Use preferences to allow users to opt-in to beta experiences:
      ...experienceFlagsPreferences,
      // Devtool can override all
      ...experienceFlagsDevtools,
    };
  }, [
    experienceFlagsSystem,
    experienceFlagsPreferences,
    experienceFlagsDevtools,
  ]);
};
